import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { X, Users, ChevronDown, Network } from "lucide-react";
import { getServices } from "../../actions/service.actions";
import ImageProfil from "../Profil/ImageProfil";

// Popup "Équipe Production" (style palette de recherche).
const TeamModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const usersData = useSelector((s) => s.usersReducer);
  const servicesData = useSelector((s) => s.serviceReducer);
  const [openRole, setOpenRole] = useState(null);
  const [showOrga, setShowOrga] = useState(false);

  useEffect(() => {
    if (open) dispatch(getServices());
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (showOrga) setShowOrga(false);
      else onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose, showOrga]);

  const services = useMemo(
    () =>
      (servicesData || []).map((s) => ({
        name: s.value,
        label: s.label,
        members: (Array.isArray(usersData) ? usersData : []).filter((u) => u.service === s.value),
      })),
    [servicesData, usersData]
  );

  if (!open) return null;

  return createPortal(
    <div
      className="palette-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="team-modal" role="dialog" aria-label="Équipe Production">
        <div className="team-modal-head">
          <span className="team-modal-title">
            <Users size={18} /> Équipe Production
          </span>
          <button className="palette-close" onClick={onClose} aria-label="Fermer">
            <X size={16} />
          </button>
        </div>

        <div className="team-modal-body">
          {services.length === 0 ? (
            <p className="team-hint">Aucun service disponible</p>
          ) : (
            services.map((service) => {
              const count = service.members.length;
              const isOpen = openRole === service.name;
              return (
                <div className={`team-role ${isOpen ? "open" : ""}`} key={service.name}>
                  <button
                    className="team-role-head"
                    onClick={() => setOpenRole(isOpen ? null : service.name)}
                  >
                    <span className="team-role-label">{service.label}</span>
                    <span className="team-role-count">{count}</span>
                    <ChevronDown size={16} className="team-role-chevron" />
                  </button>
                  {isOpen && (
                    <div className="team-role-members">
                      {count === 0 ? (
                        <p className="team-empty">Personne pour le moment</p>
                      ) : (
                        service.members.map((u) => (
                          <Link
                            className="team-member"
                            to={`/userprofil/${u.id}`}
                            key={u.id}
                            onClick={onClose}
                          >
                            <span className="team-avatar">
                              <ImageProfil id={u.id} />
                            </span>
                            <span className="team-member-info">
                              <span className="team-member-name">
                                {u.firstname} {u.name}
                              </span>
                              {u.service && <span className="team-member-role">{u.service}</span>}
                            </span>
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="team-modal-footer">
          <button className="team-orga-btn" onClick={() => setShowOrga(true)}>
            <Network size={16} /> Voir l'organigramme
          </button>
        </div>
      </div>

      {showOrga && (
        <div
          className="palette-overlay team-orga-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowOrga(false);
          }}
        >
          <div className="team-orga-modal" role="dialog" aria-label="Organigramme">
            <div className="team-modal-head">
              <span className="team-modal-title">
                <Network size={18} /> Organigramme
              </span>
              <button className="palette-close" onClick={() => setShowOrga(false)} aria-label="Fermer">
                <X size={16} />
              </button>
            </div>
            <div className="team-orga-frame">
              <iframe
                title="Organigramme"
                src="https://view.genial.ly/64c4131cffd9850019e5842f"
                frameBorder="0"
                allowFullScreen
                scrolling="yes"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default TeamModal;
